// product/SuccessScreen.jsx
// Full-screen overlay shown after a successful purchase.
// Two CTAs: "Ver mi entrega" → /tracking/:id  |  "Seguir comprando" → /
// Props:
//   deliveryPointId — number, from /api/entrega response
//   productTitle    — string
//   onKeepShopping  — callback to navigate to /

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Package, ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";
import { brand } from "../shared/brand";

export function SuccessScreen({ deliveryPointId, productTitle, onKeepShopping }) {
  // Prevent scroll on mount
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white px-6 text-center"
    >
      {/* Animated check icon */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 }}
        className="mb-6 grid size-24 place-items-center rounded-full"
        style={{ background: brand.accentGreen + "18" }}
      >
        <CheckCircle2 className="size-12" style={{ color: brand.accentGreen }} />
      </motion.div>

      {/* Heading */}
      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="text-2xl font-extrabold text-slate-900 mb-2"
      >
        ¡Pedido confirmado!
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="text-sm text-slate-500 mb-1 max-w-xs"
      >
        Tu <strong>{productTitle}</strong> está en proceso.
        Te avisaremos por WhatsApp cuando el repartidor esté en camino.
      </motion.p>

      {/* Delivery ID pill */}
      {deliveryPointId && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="mt-4 mb-8 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5"
        >
          <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wide mb-0.5">
            N° de entrega
          </p>
          <p className="text-lg font-mono font-extrabold" style={{ color: brand.primary }}>
            #{String(deliveryPointId).padStart(5, "0")}
          </p>
        </motion.div>
      )}

      {/* CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex flex-col gap-3 w-full max-w-xs"
      >
        {/* Primary — tracking page */}
        <Link
          to={`/entrega/${deliveryPointId}`}
          className="flex items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-extrabold text-white"
          style={{ background: brand.primary }}
        >
          <Package className="size-4" />
          Ver mi entrega en vivo
        </Link>

        {/* Secondary — keep shopping */}
        <button
          onClick={onKeepShopping}
          className="flex items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <ShoppingBag className="size-4" />
          Seguir comprando
        </button>
      </motion.div>

      {/* Reassurance note */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.65 }}
        className="mt-6 text-xs text-slate-400 max-w-xs"
      >
        Pagas al recibir. Nuestro repartidor cobrará el monto exacto al momento de entrega.
      </motion.p>
    </motion.div>
  );
}